/**
 * Resolve OpenClaw base data directory and provide proper commands
 * based on the detected openclaw_path.
 * 
 * openclaw_path values:
 *   - "docker:openclaw"           → /data/.openclaw (inside Docker container)
 *   - "data:/data/.openclaw"      → /data/.openclaw (direct host path)
 *   - "data:/root/.openclaw"      → /root/.openclaw
 *   - "systemd:openclaw"          → needs detection
 *   - "/usr/local/bin/openclaw"   → needs detection
 *   - null/undefined              → needs detection
 */

/**
 * Check if OpenClaw is running in Docker.
 * Matches "docker:openclaw", "docker:openclaw-ijzl-openclaw-1", etc.
 */
export function isDockerMode(openclawPath?: string): boolean {
  return !!openclawPath?.startsWith('docker:');
}

/**
 * Get the Docker container name from openclaw_path.
 * E.g. "docker:openclaw-ijzl-openclaw-1" → "openclaw-ijzl-openclaw-1"
 */
export function getDockerContainerName(openclawPath?: string): string {
  if (openclawPath?.startsWith('docker:')) {
    return openclawPath.replace('docker:', '') || 'openclaw';
  }
  return 'openclaw';
}

/**
 * Get a shell command that reads a file, handling Docker vs host mode.
 * For Docker: uses `docker exec <container> cat <path>`
 * For host: uses `cat <path>`
 */
export function getReadFileCommand(filePath: string, openclawPath?: string): string {
  if (isDockerMode(openclawPath)) {
    const container = getDockerContainerName(openclawPath);
    return `docker exec ${container} cat ${filePath} 2>/dev/null || echo "NO_FILE"`;
  }
  return `cat ${filePath} 2>/dev/null || echo "NO_FILE"`;
}

/**
 * Get a shell command that writes content to a file, handling Docker vs host.
 * Uses base64 via stdin to avoid any shell escaping issues.
 */
export function getWriteFileCommand(filePath: string, content: string, openclawPath?: string): string {
  // Encode content to base64 for safe transmission
  const b64 = btoa(unescape(encodeURIComponent(content)));
  
  // Escape single quotes for shell safety: 'text with '\'' quote' → 'text with '\'''\'' quote'
  // This closes the quote, adds escaped quote, then reopens quote
  const b64Escaped = b64.replace(/'/g, "'\\''");
  
  if (isDockerMode(openclawPath)) {
    const container = getDockerContainerName(openclawPath);
    // For Docker: pipe b64 into container, decode and write INSIDE the container
    // The redirect must be inside sh -c so it writes to the container filesystem
    return `printf '%s' '${b64Escaped}' | docker exec -i ${container} sh -c 'base64 -d > ${filePath}'`;
  }
  
  // For host: printf base64 via pipe, decode, write to file
  return `printf '%s' '${b64Escaped}' | base64 -d > ${filePath}`;
}

/**
 * Get a shell command to run inside the OpenClaw environment.
 */
export function getExecCommand(cmd: string, openclawPath?: string): string {
  if (isDockerMode(openclawPath)) {
    const container = getDockerContainerName(openclawPath);
    return `docker exec ${container} sh -c '${cmd.replace(/'/g, "'\\''")}'`;
  }
  return cmd;
}

/**
 * Get the shell command to detect the OpenClaw base directory.
 */
export function getOcBaseDetectCommand(openclawPath?: string): string {
  // Docker: config is inside container at /data/.openclaw
  if (isDockerMode(openclawPath)) {
    return 'echo "/data/.openclaw"';
  }

  // If we know it's a data dir, extract directly
  if (openclawPath?.startsWith('data:')) {
    const dir = openclawPath.replace('data:', '');
    return `echo "${dir}"`;
  }

  // For binary/systemd paths, detect by checking common locations
  return (
    'test -d /data/.openclaw/workspace && echo "/data/.openclaw" || ' +
    '(test -d $HOME/.openclaw/workspace && echo "$HOME/.openclaw") || ' +
    '(test -d /root/.openclaw/workspace && echo "/root/.openclaw") || ' +
    'echo "$HOME/.openclaw"'
  );
}

/**
 * Get all the paths for OpenClaw operations given a base directory.
 * OpenClaw uses openclaw.json as its main config file.
 */
export function getOcPaths(base: string) {
  return {
    base,
    workspace: `${base}/workspace`,
    configJson: `${base}/openclaw.json`,
    envFile: `${base}/workspace/.env`,
    // Legacy paths (for backward compat)
    configYaml: `${base}/agents/main/agent/config.yaml`,
    agentDir: `${base}/agents/main/agent`,
  };
}
